import { comunidadDelCodigoPostal, nombreDeLaComunidad } from "./comunidades"

/**
 * Como llega al comprador lo que compra en el mercado, y cuanto cuesta.
 *
 * Dos formas, y el vendedor decide cuales ofrece: envio a domicilio dentro de su
 * comunidad autonoma (tarifa fija, gratis a partir de un importe si quiere) y
 * recogida en su local.
 *
 * Todo esto son funciones puras a proposito: el importe del envio entra en lo que
 * se cobra, y lo que se cobra se calcula SIEMPRE en servidor. La misma funcion
 * pinta la cesta en el navegador y arma el pago, asi que no pueden discrepar.
 */

export type FormaDeEntrega = "envio" | "recogida"

/** Lo que el vendedor tiene configurado sobre la entrega. */
export interface EntregaDelVendedor {
  /** La comunidad autonoma del vendedor (codigo de `COMUNIDADES`). */
  comunidad: string | null
  envioActivo: boolean
  envioPrecio: number
  /** A partir de este importe en productos el envio no se cobra. `null`: nunca es gratis. */
  envioGratisDesde: number | null
  recogidaActiva: boolean
}

export function redondear(valor: number): number {
  return Math.round((Number(valor) || 0) * 100) / 100
}

/** Las formas de entrega que de verdad se pueden elegir con este vendedor. */
export function formasDisponibles(vendedor: EntregaDelVendedor): FormaDeEntrega[] {
  const formas: FormaDeEntrega[] = []
  // Sin comunidad no se puede comprobar adonde se envia, asi que no se envia.
  if (vendedor.envioActivo && vendedor.comunidad) formas.push("envio")
  if (vendedor.recogidaActiva) formas.push("recogida")
  return formas
}

/** Lo que cuesta el envio de una cesta. La recogida no cuesta nada. */
export function costeDeEnvio(vendedor: EntregaDelVendedor, forma: FormaDeEntrega, subtotal: number): number {
  if (forma !== "envio") return 0
  const precio = Math.max(0, redondear(vendedor.envioPrecio))
  const gratisDesde = vendedor.envioGratisDesde
  if (gratisDesde !== null && gratisDesde !== undefined && gratisDesde >= 0 && subtotal >= gratisDesde) return 0
  return precio
}

export type ComprobacionDeEntrega = { ok: true } | { ok: false; motivo: string }

/**
 * ¿Se puede entregar asi a esa direccion?
 *
 * El envio solo sale dentro de la comunidad del vendedor. Un codigo postal de
 * fuera no se arregla con un recargo: sencillamente no se vende con envio.
 */
export function comprobarEntrega(
  vendedor: EntregaDelVendedor,
  forma: FormaDeEntrega,
  codigoPostal: string | null | undefined,
  /** El idioma del motivo: el servidor lo da en castellano; la cesta en inglés lo pide en inglés. */
  idioma: "es" | "en" = "es",
): ComprobacionDeEntrega {
  const en = idioma === "en"
  if (!formasDisponibles(vendedor).includes(forma)) {
    return {
      ok: false,
      motivo:
        forma === "envio"
          ? en ? "This seller doesn't deliver." : "Este vendedor no hace envíos."
          : en ? "This seller doesn't offer collection in store." : "Este vendedor no ofrece recogida en el local.",
    }
  }
  if (forma === "recogida") return { ok: true }

  const destino = comunidadDelCodigoPostal(codigoPostal)
  if (!destino) return { ok: false, motivo: en ? "That postcode isn't valid." : "El código postal no es válido." }
  if (destino.codigo !== vendedor.comunidad) {
    return {
      ok: false,
      motivo: en
        ? `This seller only delivers within ${nombreDeLaComunidad(vendedor.comunidad, "en")}.`
        : `Este vendedor solo envía dentro de ${nombreDeLaComunidad(vendedor.comunidad)}.`,
    }
  }
  return { ok: true }
}

/** Una linea de la cesta ya con el precio que manda: el guardado, no el que diga el navegador. */
export interface LineaConPrecio {
  cantidad: number
  precio: number
}

export interface Importes {
  subtotal: number
  envio: number
  total: number
  /** Lo que se queda Zindra. Va dentro del total, no se le suma al comprador. */
  comision: number
}

/**
 * Los importes de un pedido.
 *
 * La comision es un porcentaje del total y sale de lo que cobra el vendedor: el
 * comprador paga lo mismo con comision o sin ella.
 */
export function importesDelPedido(params: {
  lineas: LineaConPrecio[]
  vendedor: EntregaDelVendedor
  forma: FormaDeEntrega
  comisionPorcentaje: number
}): Importes {
  const subtotal = redondear(
    params.lineas.reduce((suma, linea) => suma + redondear(linea.precio) * Math.max(0, Math.floor(linea.cantidad)), 0),
  )
  const envio = costeDeEnvio(params.vendedor, params.forma, subtotal)
  const total = redondear(subtotal + envio)
  const porcentaje = Math.min(100, Math.max(0, Number(params.comisionPorcentaje) || 0))
  const comision = redondear((total * porcentaje) / 100)
  return { subtotal, envio, total, comision }
}

export function aCentimos(importe: number): number {
  return Math.round(Math.max(0, Number(importe) || 0) * 100)
}
